import { useErrorHandler } from '@/hooks/use-error-handler';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestUserProfileByUserId } from '@/graphql/queries/onboarding';
import graphqlClient from '@/graphql/graphql-client';
import { INSERT_USER_PROFILE_MUTATION, UPDATE_USER_PROFILE_MUTATION } from '@/graphql/mutations';
import { inviteOrgMember, updateIamAccount } from '../services/onboarding.service';

const upsertProfile = async (
  userId: string,
  input: {
    JobTitle?: string;
    Department?: string;
    OnboardingCompleted?: boolean;
    OnboardingCompletedAt?: string;
    OnboardingStepsCompleted?: string[];
  }
) => {
  const existing = await requestUserProfileByUserId(userId);
  if (existing) {
    return graphqlClient.request(UPDATE_USER_PROFILE_MUTATION, {
      ItemId: existing.ItemId,
      ...input,
    });
  }
  return graphqlClient.request(INSERT_USER_PROFILE_MUTATION, {
    BlocksUserId: userId,
    ...input,
  });
};

export const useSaveProfileData = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (params: { userId: string; jobTitle?: string; department?: string }) =>
      upsertProfile(params.userId, {
        ...(params.jobTitle !== undefined && { JobTitle: params.jobTitle }),
        ...(params.department !== undefined && { Department: params.department }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'user-profiles',
      });
    },
    onError: (error) => {
      handleError(error, { variant: 'destructive' });
    },
  });
};

/**
 * Save onboarding step progress.
 */
export const useSaveOnboardingProgress = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: async (params: { userId: string; stepId: string }) => {
      const existing = await requestUserProfileByUserId(params.userId);
      const completedSteps = new Set(existing?.OnboardingStepsCompleted ?? []);
      completedSteps.add(params.stepId);
      return upsertProfile(params.userId, {
        OnboardingStepsCompleted: Array.from(completedSteps),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'user-profiles',
      });
    },
    onError: (error) => {
      handleError(error, { variant: 'destructive' });
    },
  });
};

/**
 * Mark onboarding as fully completed.
 */
export const useCompleteOnboarding = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (userId: string) =>
      upsertProfile(userId, {
        OnboardingCompleted: true,
        OnboardingCompletedAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'user-profiles',
      });
    },
    onError: (error) => {
      handleError(error, { variant: 'destructive' });
    },
  });
};

/**
 * Update IAM account (firstName, lastName).
 * Requires itemId and memberships from the user object.
 */
export const useUpdateIamAccount = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (data: {
      itemId: string;
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      profileImageUrl?: string;
      memberships: { organizationId: string; roles: string[] }[];
    }) => updateIamAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAccount'] });
    },
    onError: (error) => {
      handleError(error, { variant: 'destructive' });
    },
  });
};

// (Removed useUpdateOrganization per API migration)

/**
 * Invite a member to an organization.
 */
export const useInviteOrgMember = () => {
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (params: { orgId: string; email: string; role: string }) =>
      inviteOrgMember(params.orgId, { email: params.email, role: params.role }),
    onError: (error) => {
      handleError(error, { variant: 'destructive' });
    },
  });
};
